import { DropDownTreeChangeEvent, ItemProps } from '@progress/kendo-react-dropdowns';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { processTreeData } from 'src/utils/treeDataOperations';
import { initialPeriodCheckbookDataState } from './ViewCheckbookContext';

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'content';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

interface ClosedTagProps {
  selected?: boolean;
}

export const ClosedTag: React.FC<ClosedTagProps> = ({ selected }) => (
  <span className={`${!selected && 'bg-[#AAAAAA] text-white'} w-45 h-13 ml-1 cursor-not-allowed`}>
    <span className="m-1">CLOSED</span>
  </span>
);

const PeriodDropdown = () => {
  const {
    glPeriodIdByPeriod,
    dropDownPeriodValues,
    dropDownPeriodValue,
    setDropDownPeriodValue,
    isDropDownLoading,
    setPeriodCheckbookDataState,
    confirmBeforeProceeding,
  } = useViewCheckbook();

  const {
    isLeftBtnDropDwnDisabled,
    isRightBtnDropDwnDisabled,
    onLeftBtnDropDwnClick,
    onRightBtnDropDwnClick,
  } = usePeriodView();

  const { t } = useTranslation();
  const [expanded, setExpanded] = useState([] as number[]);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);
  const fixExpandedByglPeriod = useCallback(() => {
    setExpanded(
      glPeriodIdByPeriod && dropDownPeriodValue?.fiscalYear ? [dropDownPeriodValue.fiscalYear] : []
    );
  }, [dropDownPeriodValue?.fiscalYear, glPeriodIdByPeriod]);

  const onChange = useCallback(
    (event: DropDownTreeChangeEvent) => {
      const { level = [], value = {} } = event;
      const { fiscalYear = 0 } = value || {};
      if (isEmpty(level) || level.length > 1) {
        confirmBeforeProceeding(() => {
          setDropDownOpened(false);
          setDropDownPeriodValue(event.value);
          setPeriodCheckbookDataState((prev) => ({
            ...initialPeriodCheckbookDataState,
            take: prev.take,
          }));
        });
      } else if (level.length === 1) {
        setExpanded((prev) => {
          if (prev.includes(fiscalYear)) {
            return prev.filter((item) => item !== fiscalYear);
          } else {
            return [...prev, fiscalYear];
          }
        });
      }
    },
    [confirmBeforeProceeding, setDropDownPeriodValue, setPeriodCheckbookDataState]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { content, parent = false, isClosed } = props.item;
      const isPeriodClosed = isClosed === 1;

      const isSelected = props.item.glPeriodId === dropDownPeriodValue?.glPeriodId;
      return (
        <>
          {parent ? (
            <span onClick={() => setDropDownOpened(true)}>
              {content}
              {isPeriodClosed && <ClosedTag />}
            </span>
          ) : isSelected ? (
            <span
              className={'p-1'}
              style={{ color: '#ffffff', cursor: 'not-allowed', backgroundColor: '#0E80B4' }}
              id="checkbook-budget-period-popup-selected-item"
            >
              {content}
              {isPeriodClosed && <ClosedTag selected={true} />}
            </span>
          ) : (
            <>
              <span className={`cursor-default hover:cursor-pointer`}>
                {content}
                {isPeriodClosed && <ClosedTag />}
              </span>
            </>
          )}
        </>
      );
    },
    [dropDownPeriodValue?.glPeriodId]
  );

  const treeData = useMemo(
    () => processTreeData(dropDownPeriodValues, { expanded, dropDownPeriodValue }, fields),
    [dropDownPeriodValues, expanded, dropDownPeriodValue]
  );

  useEffect(() => {
    if (dropDownOpened) {
      document
        .getElementById('checkbook-budget-period-popup-selected-item')
        ?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dropDownOpened]);

  const valueRenderer = useCallback((value: any) => {
    if (!value) return <></>;
    const { content, isClosed } = value?.item || {};
    const isPeriodClosed = isClosed === 1;
    return (
      <span>
        {content}
        {isPeriodClosed && (
          <span className="bg-[#AAAAAA] text-white w-45 h-13 ml-2">
            <span className="m-1">CLOSED</span>
          </span>
        )}
      </span>
    );
  }, []);

  return (
    <Tooltip anchorElement="target" position="bottom" parentTitle>
      <div className="flex items-center flex-wrap">
        <span className="text-xs mr-2">{t('Select a Fiscal Year and Period')}</span>
        <div>
          <span title={isLeftBtnDropDwnDisabled ? 'No Previous Period' : 'Previous Period'}>
            <CircularIconButton
              icon="arrow-end-left"
              disabled={isLeftBtnDropDwnDisabled}
              onClick={onLeftBtnDropDwnClick}
            />
          </span>
          <TreeDropDown
            expanded={expanded}
            setExpanded={setExpanded}
            dropDownOpened={dropDownOpened}
            setDropDownOpened={setDropDownOpened}
            id="period-dropdown"
            style={{ width: '320px' }}
            data={treeData}
            value={dropDownPeriodValue}
            placeholder={t('Please select ...')}
            textField={textField}
            dataItemKey={dataItemKey}
            selectField={selectField}
            expandField={expandField}
            loading={isDropDownLoading}
            setInitialExpandValue={fixExpandedByglPeriod}
            onChange={onChange}
            item={Item}
            className="checkbook-period-dropdown"
            popupSettings={{
              popupClass: 'max-h-[60vh]',
            }}
            valueRenderer={valueRenderer}
          />
          <span title={isRightBtnDropDwnDisabled ? 'No Future Period' : 'Next Period'}>
            <CircularIconButton
              icon="arrow-end-right"
              disabled={isRightBtnDropDwnDisabled}
              onClick={onRightBtnDropDwnClick}
            />
          </span>
        </div>
      </div>
    </Tooltip>
  );
};

export default PeriodDropdown;
